import {Avatar, Box, Checkbox, Radio, Rating, Switch} from '@mui/material';
import dayjs from 'dayjs';
import AppProgressBar from 'main/core/AppFormComponents/AppProgressBar';
import AppProgressText from 'main/core/AppFormComponents/AppProgressText';
import {useSelector} from 'react-redux';
import {
  UiWiseBooleanControl,
  UiWiseFieldFormat,
} from 'shared/constants/UiWiseConst';
import {isEmpty, isTrue, notEmpty} from '../CommonUtil';

export const formatDateTime = (value = 0, pattern = 'YYYY-MM-DD') => {
  const dateObj = dayjs(value);
  return dateObj.isValid() ? dateObj.format(pattern) : '';
};

export const formatNumber = (value = 0, pattern = '9.') => {
  const numValue = Number(value);
  if (isNaN(numValue)) return '0';
  if (isEmpty(pattern)) return value;
  // 1. Parse the pattern
  const [wholePattern, decimalPattern] = pattern.split('.');

  // Count '9's to determine constraints
  const minWholeDigits = wholePattern.replace(/,/g, '').length;
  const decimalPlaces = decimalPattern ? decimalPattern.length : 0;
  const useGrouping = wholePattern.includes(',');

  // 2. Use the Internationalization API for precise formatting
  return new Intl.NumberFormat('en-US', {
    minimumIntegerDigits: minWholeDigits,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: useGrouping,
  }).format(numValue);
};

export function getColor(match, pattern) {
  if (!pattern) return '';

  // Split OR rules
  const rules = pattern.split('||');

  for (const rule of rules) {
    const [key, value] = getKeyValue(rule);

    // default rule
    if (key === 'default') {
      return value;
    }

    // numeric comparison (your existing behavior)
    if (!isNaN(+match) && !isNaN(+key)) {
      if (+match <= +key) {
        return value;
      }
      continue;
    }

    // multiple string keys (comma-separated)
    const keys = key.split(',').map((k) => k.trim());

    if (keys.includes(match)) {
      return value;
    }
  }
  return '';
}

function getKeyValue(pattern, separator = '|') {
  const pair = pattern.split(separator);

  if (pair.length === 1) {
    return ['default', pair[0]];
  }

  return [pair[0].trim(), pair[1].trim()];
}

export const getFormattedValue = (field, content, bgColor, textColor) => {
  // console.log(`[getFormattedValue] name: ${field.name}, format: ${field.format}, pattern: ${field.pattern}, content: ${content}`,
  // );

  const value = content ?? '';
  if (value === '-') {
    return value;
  }

  const safePrefix = notEmpty(field.prefix) ? field.prefix.trim() + ' ' : '';
  const safeSuffix = notEmpty(field.suffix) ? ' ' + field.suffix.trim() : '';
  switch (field.format) {
    case UiWiseFieldFormat.RATING:
      return (
        <Rating
          name={field.name}
          value={parseFloat(value) || 0}
          precision={field.precision}
          max={5}
          readOnly
        />
      );

    case UiWiseFieldFormat.PROGRESSBAR:
      return (
        <AppProgressBar
          borderRadius={0}
          thickness={25}
          pathColor='#F0F0F0'
          activeColor={bgColor}
          variant='determinate'
          value={Number(value) || 0}
        />
      );

    case UiWiseFieldFormat.PROGRESSTEXT:
      return (
        <AppProgressText
          borderRadius={0}
          thickness={25}
          pathColor='#F0F0F0'
          activeColor={bgColor}
          textColor={textColor}
          variant='determinate'
          value={Number(value) || 0}
          pattern={field.pattern}
          prefix={safePrefix}
          suffix={safeSuffix}
        />
      );

    case UiWiseFieldFormat.DATE: {
      // Prevent "Invalid Date" strings in the UI
      return formatDateTime(value, field.pattern);
    }

    case UiWiseFieldFormat.NUMBER:
    case UiWiseFieldFormat.CURRENCY:
    case UiWiseFieldFormat.PERCENTAGE:
      return `${safePrefix}${formatNumber(value, field.pattern)}${safeSuffix}`;

    case UiWiseFieldFormat.BOOLEAN: {
      const boolProps = {
        size: 'small',
        checked: isTrue(value),
        readonly: true,
        sx: {
          color: bgColor,
          '&.Mui-checked': {color: bgColor},
          padding: 0,
        },
      };

      const controls = {
        [UiWiseBooleanControl.RADIO]: <Radio {...boolProps} />,
        [UiWiseBooleanControl.CHECKBOX]: <Checkbox {...boolProps} />,
        [UiWiseBooleanControl.SWITCH]: <Switch {...boolProps} />,
      };

      return controls[field.pattern] || null;
    }
    case UiWiseFieldFormat.THUMBNAIL: {
      const appEndPoint = useSelector(
        ({commonReducer}) => commonReducer.appEndPoint,
      );

      return (
        <Box
          sx={{
            color: textColor || 'primary.main',
            bgcolor: bgColor,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            sx={{
              height: 32,
              width: 32,
              mr: 3,
            }}
            src={appEndPoint + '/' + value.trim()}
          />
        </Box>
      );
    }
    default:
      return value;
  }
};
